import React, { useEffect, useState, useMemo } from "react";
import { useDispatch } from "react-redux";
import { setActivePage, setShowPreloader } from "../store/activePage";
import DataTable from "react-data-table-component";
import formatCustomDate from "../helpers/customDateFormat";
import { AxiosGetMethod, AxiosPostMethod } from "../helpers/customAxios";
import HandleLogout from "../hooks/handleLogout";
import { ErrorToast, SuccessToast } from "../components/toastify";
import { useFormik } from "formik";
import * as Yup from "yup";

const exportTextDecoder =
  typeof TextDecoder !== "undefined"
    ? new TextDecoder("utf-8", { fatal: false })
    : null;

const normalizeExportText = (value) => {
  const raw = String(value ?? "");
  if (!raw || !exportTextDecoder) return raw;

  const looksMojibake = /(?:Ã.|Â.|â.|à.|ðŸ)/.test(raw);
  if (!looksMojibake) return raw;

  try {
    const bytes = Uint8Array.from(
      Array.from(raw, (char) => char.charCodeAt(0) & 0xff),
    );
    const repaired = exportTextDecoder.decode(bytes);
    return repaired.includes("\uFFFD") ? raw : repaired;
  } catch (error) {
    return raw;
  }
};

const exportRowsToExcel = (title, columns, rows, fileName) => {
  const tableHead = columns
    .map((column) => `<th style="border:1px solid #cbd5e1;padding:10px;background:#f8fafc;color:#1e293b;font-weight:bold;">${escapeHtml(column.header)}</th>`)
    .join("");
  const tableBody = rows
    .map(
      (row) =>
        `<tr>${columns
          .map(
            (column) =>
              `<td style="border:1px solid #d1d5db;padding:10px;">${escapeHtml(column.getValue(row))}</td>`,
          )
          .join("")}</tr>`,
    )
    .join("");

  const html = `
    <html lang="ta" xmlns:o="urn:schemas-microsoft-com:office:office"
      xmlns:x="urn:schemas-microsoft-com:office:excel"
      xmlns="http://www.w3.org/TR/REC-html40">
      <head>
        <meta charset="utf-8" />
        <meta http-equiv="Content-Type" content="text/html; charset=utf-8" />
        <style>
          body { font-family: "Noto Sans Tamil", "Latha", "Vijaya", Arial, sans-serif; }
        </style>
      </head>
      <body>
        <h2>${escapeHtml(title)}</h2>
        <table style="border-collapse:collapse;width:100%;">
          <thead><tr>${tableHead}</tr></thead>
          <tbody>${tableBody}</tbody>
        </table>
      </body>
    </html>
  `;

  const blob = new Blob([`\ufeff${html}`], {
    type: "application/vnd.ms-excel;charset=utf-8;",
  });
  const link = document.createElement("a");
  const url = URL.createObjectURL(blob);
  link.href = url;
  link.download = `${fileName}.xls`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

const exportRowsToPdf = (title, columns, rows) => {
  const tableHead = columns
    .map(
      (column) =>
        `<th style="border:1px solid #cbd5e1;padding:8px;background:#f8fafc;color:#1e293b;text-align:left;font-weight:bold;">${escapeHtml(column.header)}</th>`,
    )
    .join("");
  const tableBody = rows
    .map(
      (row) =>
        `<tr>${columns
          .map(
            (column) =>
              `<td style="border:1px solid #d1d5db;padding:8px;text-align:left;">${escapeHtml(column.getValue(row))}</td>`,
          )
          .join("")}</tr>`,
    )
    .join("");

  const printWindow = window.open("", "_blank", "width=1200,height=800");
  if (!printWindow) {
    ErrorToast("Popup blocked. Please allow popups to export PDF.");
    return;
  }

  printWindow.document.write(`
    <html lang="ta">
      <head>
        <title>${escapeHtml(title)}</title>
        <meta charset="utf-8" />
        <style>
          body { font-family: "Noto Sans Tamil", "Latha", "Vijaya", Arial, sans-serif; padding: 24px; color: #0f172a; }
          h1 { font-size: 22px; margin-bottom: 6px; }
          p { color: #475569; margin-top: 0; margin-bottom: 16px; }
          table { width: 100%; border-collapse: collapse; font-size: 12px; }
          @media print { body { padding: 0; } }
        </style>
      </head>
      <body>
        <h1>${escapeHtml(title)}</h1>
        <p>Generated on ${escapeHtml(new Date().toLocaleString("en-IN"))}</p>
        <table>
          <thead><tr>${tableHead}</tr></thead>
          <tbody>${tableBody}</tbody>
        </table>
      </body>
    </html>
  `);
  printWindow.document.close();
  printWindow.focus();
  setTimeout(() => {
    printWindow.print();
  }, 300);
};

const escapeHtml = (value) =>
  normalizeExportText(value)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");

const formatMoney = (value) =>
  `₹${(Number(value) || 0).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const buildMoiExportColumns = () => [
  { header: "Event Date", getValue: (row) => formatCustomDate(row.event_date) },
  { header: "Event Type", getValue: (row) => row.event_type || "—" },
  { header: "Name", getValue: (row) => row.name || "—" },
  { header: "Father / Husband Name", getValue: (row) => row.family_name || "—" },
  { header: "Amount", getValue: (row) => (row.amount ? formatMoney(row.amount) : "—") },
  { header: "Asset", getValue: (row) => row.asset_name || "—" },
  { header: "Village", getValue: (row) => row.village || "—" },
  { header: "Mobile", getValue: (row) => row.mobile || "—" },
  { header: "Source", getValue: (row) => row.source || "—" },
];

// ─── Validation Schema ────────────────────────────────────────────────────────
const moiValidationSchema = Yup.object({
  event_date: Yup.string().nullable(),
  event_type: Yup.string().nullable(),
  name: Yup.string().nullable(),
  family_name: Yup.string().nullable(),
  amount: Yup.number()
    .transform((value, originalValue) =>
      String(originalValue).trim() === "" ? null : value
    )
    .nullable()
    .typeError("Amount must be a number")
    .positive("Amount must be positive"),
  asset_name: Yup.string().nullable(),
  village: Yup.string().nullable(),
  mobile: Yup.string()
    .nullable()
    .test("is-valid-mobile", "Mobile must be 10 digits", (val) => {
      if (!val || String(val).trim() === "") return true;
      return /^[0-9]{10}$/.test(String(val).trim());
    }),
});

// ─── Reusable FormField ───────────────────────────────────────────────────────
function FormField({ label, name, type = "text", placeholder, required, formik }) {
  return (
    <div className="flex flex-col gap-1.5">
      <label className="text-xs font-bold text-slate-700">
        {label} {required && <span className="text-rose-500">*</span>}
      </label>
      <input
        type={type}
        name={name}
        placeholder={placeholder}
        value={formik.values[name]}
        onChange={formik.handleChange}
        onBlur={formik.handleBlur}
        className={`p-2.5 border rounded-xl text-xs bg-slate-50 focus:bg-white focus:outline-none focus:ring-2 focus:ring-amber-500/20 focus:border-amber-600 transition
          ${formik.touched[name] && formik.errors[name] ? "border-rose-400 bg-rose-50/50" : "border-slate-200"}`}
      />
      {formik.touched[name] && formik.errors[name] && (
        <p className="text-[10px] text-rose-500 font-semibold">{formik.errors[name]}</p>
      )}
    </div>
  );
}

// ─── Add Moi Modal ────────────────────────────────────────────────────────────
function AddMoiModal({ isOpen, onClose, onSuccess, moiType }) {
  const [eventTypes, setEventTypes] = useState([]);
  const [showCustomType, setShowCustomType] = useState(false);

  useEffect(() => {
    if (isOpen) {
      (async () => {
        try {
          const res = await AxiosGetMethod("customer/event-type-list");
          if (res?.status === 200 && res.data?.data) {
            setEventTypes(res.data.data);
          }
        } catch {
          // silently fail, user can still type custom
        }
      })();
    }
  }, [isOpen]);

  const formik = useFormik({
    initialValues: {
      event_date: "", event_type: "", name: "", family_name: "",
      amount: "", asset_name: "", village: "", mobile: "",
    },
    validationSchema: moiValidationSchema,
    onSubmit: async (values, { setSubmitting, resetForm }) => {
      try {
        const response = await AxiosPostMethod("customer/add-moi-note", {
          ...values,
          moi_type: moiType,
        });
        if (response?.status === 200 || response?.status === 201) {
          SuccessToast("Moi entry added successfully!");
          resetForm();
          setShowCustomType(false);
          onSuccess();
          onClose();
        } else {
          if (response?.status === 401) HandleLogout();
          ErrorToast("Failed to add Moi entry. Please try again.");
        }
      } catch {
        ErrorToast("Something went wrong. Please try again.");
      } finally {
        setSubmitting(false);
      }
    },
  });

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <div>
            <h3 className="text-lg font-semibold text-gray-800">
              Add {moiType === "written" ? "Money You Gave" : "Money You Got"}
            </h3>
            <p className="text-xs text-gray-400 mt-0.5">
              Type: <span className={`font-medium ${moiType === "written" ? "text-rose-500" : "text-emerald-500"}`}>
                {moiType === "written" ? "You Gave" : "You Got"}
              </span>
            </p>
          </div>
          <button onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-full text-gray-400 hover:bg-gray-100 hover:text-gray-600 transition text-xl">
            &times;
          </button>
        </div>
        <form onSubmit={formik.handleSubmit} className="px-6 py-5">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <FormField label="Event Date" name="event_date" type="date" formik={formik} />

            {/* Event Type Select */}
            <div className="flex flex-col gap-1.5">
              <label className="text-xs font-bold text-slate-700">
                Event Type
              </label>
              {!showCustomType ? (
                <select
                  name="event_type"
                  value={formik.values.event_type}
                  onChange={(e) => {
                    if (e.target.value === "__custom__") {
                      setShowCustomType(true);
                      formik.setFieldValue("event_type", "");
                    } else {
                      formik.handleChange(e);
                    }
                  }}
                  onBlur={formik.handleBlur}
                  className={`p-2.5 border rounded-xl text-xs bg-slate-50 focus:bg-white focus:outline-none focus:ring-2 focus:ring-amber-500/20 focus:border-amber-600 transition
                    ${formik.touched.event_type && formik.errors.event_type ? "border-rose-400 bg-rose-50/50" : "border-slate-200"}`}
                >
                  <option value="">Select event type</option>
                  {eventTypes.map((et, idx) => (
                    <option key={idx} value={et.event_name}>{et.event_name}</option>
                  ))}
                  <option value="__custom__">✏️ Custom...</option>
                </select>
              ) : (
                <div className="flex gap-2">
                  <input
                    type="text"
                    name="event_type"
                    placeholder="Enter custom event type"
                    value={formik.values.event_type}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    className={`flex-1 p-2.5 border rounded-xl text-xs bg-slate-50 focus:bg-white focus:outline-none focus:ring-2 focus:ring-amber-500/20 focus:border-amber-600 transition
                      ${formik.touched.event_type && formik.errors.event_type ? "border-rose-400 bg-rose-50/50" : "border-slate-200"}`}
                  />
                  <button
                    type="button"
                    onClick={() => { setShowCustomType(false); formik.setFieldValue("event_type", ""); }}
                    className="px-2.5 py-1.5 text-[10px] font-bold rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-600 transition"
                  >
                    List
                  </button>
                </div>
              )}
              {formik.touched.event_type && formik.errors.event_type && (
                <p className="text-[10px] text-rose-500 font-semibold">{formik.errors.event_type}</p>
              )}
            </div>

            <FormField label="Host Name" name="name" placeholder="Enter host name" formik={formik} />
            <FormField label="Father / Husband Name" name="family_name" placeholder="Enter father / husband name" formik={formik} />
            <FormField label="Amount (₹)" name="amount" type="number" placeholder="Enter amount" formik={formik} />
            <FormField label="Asset Name" name="asset_name" placeholder="e.g. Gold, Land" formik={formik} />
            {formik.touched.amount && formik.errors.amount && (
              <p className="col-span-2 -mt-2 text-xs text-rose-500">{formik.errors.amount}</p>
            )}
            <FormField label="Village" name="village" placeholder="Enter village name" formik={formik} />
            <FormField label="Mobile Number" name="mobile" type="tel" placeholder="10-digit mobile number" formik={formik} />
          </div>
          <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-slate-100">
            <button type="button" onClick={() => { formik.resetForm(); setShowCustomType(false); onClose(); }}
              className="px-5 py-2 rounded-xl border border-slate-200 text-slate-600 text-xs font-bold hover:bg-slate-50 transition shadow-sm">
              Cancel
            </button>
            <button type="submit" disabled={formik.isSubmitting}
              className="px-5 py-2 rounded-xl bg-amber-600 text-white text-xs font-bold hover:bg-amber-700 transition focus:outline-none focus:ring-2 focus:ring-amber-500/20 disabled:opacity-60 disabled:cursor-not-allowed flex items-center gap-2 shadow-sm">
              {formik.isSubmitting ? (
                <><svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                </svg>Saving...</>
              ) : "Save Entry"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─── Tally Entry Row ──────────────────────────────────────────────────────────
function TallyRow({ r, amountColor }) {
  const hasAmount = r.amount && parseFloat(r.amount) > 0;
  const hasAsset  = r.asset_name && r.asset_name.trim() !== "";
  return (
    <div className="flex justify-between items-center bg-slate-50 rounded-xl px-4 py-3 border border-slate-100/80 transition hover:border-slate-200">
      <div>
        <p className="text-xs font-semibold text-slate-700">{r.name}</p>
        <p className="text-[10px] text-slate-400 mt-0.5">{formatCustomDate(r.event_date)} · {r.event_type}</p>
      </div>
      <div className="text-right">
        {hasAmount && (
          <p className={`text-xs font-bold ${amountColor}`}>
            {formatMoney(r.amount)}
          </p>
        )}
        {hasAsset && (
          <span className="inline-flex items-center gap-1 mt-1 px-2 py-0.5 rounded-full bg-amber-50 border border-amber-200 text-[10px] font-bold text-amber-700">
            🎁 {r.asset_name}
          </span>
        )}
      </div>
    </div>
  );
}

// ─── Tally Modal ──────────────────────────────────────────────────────────────
function TallyModal({ isOpen, onClose, writtenData, receivedData }) {
  const [searchMobile, setSearchMobile] = useState("");
  const [searched, setSearched] = useState(false);

  const tally = useMemo(() => {
    if (!searched || searchMobile.length < 10) return null;

    const written  = writtenData.filter((r) => r.mobile === searchMobile);
    const received = receivedData.filter((r) => r.mobile === searchMobile);

    // Cash entries (have a numeric amount > 0)
    const writtenCash   = written.filter((r)  => r.amount && parseFloat(r.amount) > 0);
    const receivedCash  = received.filter((r) => r.amount && parseFloat(r.amount) > 0);

    // Asset-only entries (no amount, only asset_name)
    const writtenAssets  = written.filter((r)  => (!r.amount || parseFloat(r.amount) === 0) && r.asset_name);
    const receivedAssets = received.filter((r) => (!r.amount || parseFloat(r.amount) === 0) && r.asset_name);

    // Mixed entries (have both amount AND asset — list under cash but badge the asset too)
    const totalWritten  = writtenCash.reduce((s, r)  => s + (parseFloat(r.amount) || 0), 0);
    const totalReceived = receivedCash.reduce((s, r) => s + (parseFloat(r.amount) || 0), 0);
    const balance       = totalWritten - totalReceived;

    return {
      written, received,
      writtenCash, receivedCash,
      writtenAssets, receivedAssets,
      totalWritten, totalReceived, balance,
      hasAssets: writtenAssets.length > 0 || receivedAssets.length > 0,
    };
  }, [searched, searchMobile, writtenData, receivedData]);

  const handleSearch = () => {
    if (searchMobile.length === 10) setSearched(true);
    else ErrorToast("Enter a valid 10-digit mobile number");
  };

  const reset = () => { setSearchMobile(""); setSearched(false); };
  const handleClose = () => { onClose(); reset(); };
  const tallyExportColumns = [
    { header: "Direction", getValue: (row) => row.direction },
    { header: "Event Date", getValue: (row) => formatCustomDate(row.event_date) },
    { header: "Event Type", getValue: (row) => row.event_type || "—" },
    { header: "Name", getValue: (row) => row.name || "—" },
    { header: "Father / Husband Name", getValue: (row) => row.family_name || "—" },
    { header: "Amount", getValue: (row) => (row.amount ? formatMoney(row.amount) : "—") },
    { header: "Asset", getValue: (row) => row.asset_name || "—" },
    { header: "Village", getValue: (row) => row.village || "—" },
    { header: "Mobile", getValue: (row) => row.mobile || "—" },
  ];
  const tallyExportRows = tally
    ? [
        ...tally.written.map((row) => ({ ...row, direction: "You Gave" })),
        ...tally.received.map((row) => ({ ...row, direction: "You Received" })),
      ]
    : [];

  if (!isOpen) return null;

  const hasResults = tally && (tally.written.length > 0 || tally.received.length > 0);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={handleClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-3xl mx-4 max-h-[90vh] overflow-y-auto">

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <div>
            <h3 className="text-lg font-semibold text-gray-800">Check Person Summary</h3>
            <p className="text-xs text-gray-400 mt-0.5">Search by mobile number to see money you gave and money you got</p>
          </div>
          <button onClick={handleClose}
            className="w-8 h-8 flex items-center justify-center rounded-full text-gray-400 hover:bg-gray-100 transition text-xl">
            &times;
          </button>
        </div>

        <div className="px-6 py-5">
          {/* Search Row */}
          <div className="flex gap-3 mb-6">
            <input
              type="tel"
              maxLength={10}
              value={searchMobile}
              onChange={(e) => { setSearchMobile(e.target.value.replace(/\D/g, "")); setSearched(false); }}
              onKeyDown={(e) => e.key === "Enter" && handleSearch()}
              placeholder="Enter 10-digit mobile number"
              className="flex-1 p-2.5 border border-slate-200 rounded-xl text-xs bg-slate-50 focus:bg-white focus:outline-none focus:ring-2 focus:ring-amber-500/20 focus:border-amber-600 transition"
            />
            <button onClick={handleSearch}
              className="px-5 py-2.5 bg-amber-600 text-white text-xs font-bold rounded-xl hover:bg-amber-700 transition focus:outline-none focus:ring-2 focus:ring-amber-500/20 shadow-sm">
              Search
            </button>
            {searched && (
              <button onClick={reset}
                className="px-4 py-2.5 border border-slate-200 text-slate-500 text-xs font-bold rounded-xl hover:bg-slate-50 transition shadow-sm">
                Clear
              </button>
            )}
          </div>

          {searched && hasResults && (
            <div className="flex flex-wrap gap-2 mb-5">
              <button
                onClick={() =>
                  exportRowsToPdf(
                    `Check Person Summary - ${searchMobile}`,
                    tallyExportColumns,
                    tallyExportRows,
                  )
                }
                className="px-3 py-2 border border-slate-200 bg-white hover:bg-slate-50 text-slate-700 text-xs font-bold rounded-xl transition shadow-sm"
              >
                Export PDF
              </button>
              <button
                onClick={() =>
                  exportRowsToExcel(
                    `Check Person Summary - ${searchMobile}`,
                    tallyExportColumns,
                    tallyExportRows,
                    `check-person-${searchMobile}`,
                  )
                }
                className="px-3 py-2 border border-slate-200 bg-white hover:bg-slate-50 text-slate-700 text-xs font-bold rounded-xl transition shadow-sm"
              >
                Export Excel
              </button>
            </div>
          )}

          {/* Results */}
          {searched && hasResults && (
            <>
              {/* ── Cash Summary Cards ── */}
              <div className="grid grid-cols-3 gap-4 mb-2">
                <div className="bg-rose-50/50 border border-rose-100/60 rounded-xl p-4 text-center">
                  <p className="text-[10px] text-rose-500 font-bold uppercase tracking-wider mb-1">You Gave</p>
                  <p className="text-xl font-bold text-rose-600">{formatMoney(tally.totalWritten)}</p>
                  <p className="text-[10px] text-rose-400 mt-1">{tally.writtenCash.length} cash entr{tally.writtenCash.length === 1 ? "y" : "ies"}</p>
                </div>
                <div className="bg-emerald-50/50 border border-emerald-100/60 rounded-xl p-4 text-center">
                  <p className="text-[10px] text-emerald-600 font-bold uppercase tracking-wider mb-1">You Received</p>
                  <p className="text-xl font-bold text-emerald-600">{formatMoney(tally.totalReceived)}</p>
                  <p className="text-[10px] text-emerald-500/70 mt-1">{tally.receivedCash.length} cash entr{tally.receivedCash.length === 1 ? "y" : "ies"}</p>
                </div>
                <div className={`rounded-xl p-4 text-center border ${
                  tally.balance === 0 ? "bg-slate-50 border-slate-100"
                  : tally.balance > 0 ? "bg-emerald-50/50 border-emerald-100/60"
                  : "bg-rose-50/50 border-rose-100/60"
                }`}>
                  <p className={`text-[10px] font-bold uppercase tracking-wider mb-1 ${
                    tally.balance === 0 ? "text-slate-400" : tally.balance > 0 ? "text-emerald-600" : "text-rose-600"
                  }`}>
                    {tally.balance === 0 ? "No Balance" : tally.balance > 0 ? "They Owe You" : "You Owe Them"}
                  </p>
                  <p className={`text-xl font-bold ${
                    tally.balance === 0 ? "text-slate-500" : tally.balance > 0 ? "text-emerald-600" : "text-rose-600"
                  }`}>
                    {tally.balance === 0 ? "✓" : formatMoney(Math.abs(tally.balance))}
                  </p>
                  <p className={`text-[10px] mt-1 ${
                    tally.balance === 0 ? "text-slate-400" : tally.balance > 0 ? "text-emerald-500/70" : "text-rose-500/70"
                  }`}>
                    {tally.balance === 0 ? "Nothing pending" : "Money entries only"}
                  </p>
                </div>
              </div>

              {/* ── Asset summary note below cards ── */}
              {tally.hasAssets && (
                <div className="flex items-start gap-2 mb-5 px-3 py-2.5 bg-amber-50 border border-amber-200 rounded-xl">
                  <span className="text-base mt-0.5">🎁</span>
                  <p className="text-xs text-amber-700 leading-relaxed">
                    This person also has <strong>gift item</strong> entries that are not added to the money total.
                    {tally.writtenAssets.length > 0 && ` You gave ${tally.writtenAssets.length} asset gift${tally.writtenAssets.length > 1 ? "s" : ""}.`}
                    {tally.receivedAssets.length > 0 && ` You received ${tally.receivedAssets.length} asset gift${tally.receivedAssets.length > 1 ? "s" : ""}.`}
                    {" "}See details below.
                  </p>
                </div>
              )}

              {/* ── Transaction Detail Lists ── */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                {/* Written */}
                <div>
                  <h4 className="text-sm font-semibold text-gray-600 mb-3 flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full bg-rose-400 inline-block" />
                    Entries You Added As Given ({tally.written.length})
                  </h4>
                  {tally.written.length === 0 ? (
                    <p className="text-xs text-gray-400 italic">No entries</p>
                  ) : (
                    <div className="space-y-2">
                      {tally.written.map((r, i) => (
                        <TallyRow key={i} r={r} amountColor="text-rose-500" />
                      ))}
                    </div>
                  )}
                </div>

                {/* Received */}
                <div>
                  <h4 className="text-sm font-semibold text-gray-600 mb-3 flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full bg-emerald-400 inline-block" />
                    Entries You Added As Got ({tally.received.length})
                  </h4>
                  {tally.received.length === 0 ? (
                    <p className="text-xs text-gray-400 italic">No entries</p>
                  ) : (
                    <div className="space-y-2">
                      {tally.received.map((r, i) => (
                        <TallyRow key={i} r={r} amountColor="text-emerald-500" />
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* ── Asset-only section (grouped separately at bottom) ── */}
              {tally.hasAssets && (
                <div className="mt-6 pt-5 border-t border-gray-100">
                  <h4 className="text-sm font-semibold text-gray-600 mb-3 flex items-center gap-2">
                    <span className="text-base">🎁</span>
                    Gift Items (not included in money total)
                  </h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {/* Assets Given */}
                    {tally.writtenAssets.length > 0 && (
                      <div>
                        <p className="text-xs font-semibold text-rose-400 uppercase tracking-wide mb-2">You Gave</p>
                        <div className="space-y-2">
                          {tally.writtenAssets.map((r, i) => (
                            <div key={i} className="flex justify-between items-center bg-amber-50 border border-amber-100 rounded-xl px-3 py-2.5">
                              <div>
                                <p className="text-sm font-medium text-gray-700">{r.name}</p>
                                <p className="text-xs text-gray-400">{formatCustomDate(r.event_date)} · {r.event_type}</p>
                              </div>
                              <span className="text-xs font-semibold text-amber-600 bg-white border border-amber-200 px-2 py-1 rounded-lg">
                                {r.asset_name}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    {/* Assets Received */}
                    {tally.receivedAssets.length > 0 && (
                      <div>
                        <p className="text-xs font-semibold text-emerald-500 uppercase tracking-wide mb-2">You Received</p>
                        <div className="space-y-2">
                          {tally.receivedAssets.map((r, i) => (
                            <div key={i} className="flex justify-between items-center bg-amber-50 border border-amber-100 rounded-xl px-3 py-2.5">
                              <div>
                                <p className="text-sm font-medium text-gray-700">{r.name}</p>
                                <p className="text-xs text-gray-400">{formatCustomDate(r.event_date)} · {r.event_type}</p>
                              </div>
                              <span className="text-xs font-semibold text-amber-600 bg-white border border-amber-200 px-2 py-1 rounded-lg">
                                {r.asset_name}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </>
          )}

          {/* Empty State */}
          {searched && !hasResults && (
            <div className="text-center py-12 text-gray-400">
              <p className="text-4xl mb-3">🔍</p>
              <p className="text-sm font-medium">No Moi entries found for this number.</p>
              <p className="text-xs mt-1">Try a different mobile number.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Summary Bar ──────────────────────────────────────────────────────────────
function SummaryBar({ data, type }) {
  const total = data.reduce((s, r) => s + (parseFloat(r.amount) || 0), 0);
  const isWritten = type === "written";
  return (
    <div className={`flex items-center gap-6 px-5 py-3 rounded-xl mb-4 ${
      isWritten ? "bg-rose-50 border border-rose-100" : "bg-emerald-50 border border-emerald-100"
    }`}>
      <div>
        <p className={`text-xs font-semibold uppercase tracking-wide ${isWritten ? "text-rose-400" : "text-emerald-500"}`}>
          Total {isWritten ? "Given" : "Received"}
        </p>
        <p className={`text-xl font-bold ${isWritten ? "text-rose-600" : "text-emerald-600"}`}>
          {formatMoney(total)}
        </p>
      </div>
      <div className={`h-8 w-px ${isWritten ? "bg-rose-200" : "bg-emerald-200"}`} />
      <div>
        <p className={`text-xs font-semibold uppercase tracking-wide ${isWritten ? "text-rose-400" : "text-emerald-500"}`}>
          Entries
        </p>
        <p className={`text-xl font-bold ${isWritten ? "text-rose-600" : "text-emerald-600"}`}>
          {data.length}
        </p>
      </div>
    </div>
  );
}

// ─── Pending Summary Modal ────────────────────────────────────────────────────
function PendingModal({ isOpen, onClose, writtenData, receivedData }) {
  const [filterTab, setFilterTab] = useState("all"); // "all" | "owe" | "owed"
  const [sortBy, setSortBy] = useState("balance");   // "balance" | "name"

  // Build per-person summary keyed by "mobile||name"
  const summary = useMemo(() => {
    const map = {};

    const upsert = (mobile, name, type, amount) => {
      const key = `${mobile}||${name}`;
      if (!map[key]) map[key] = { mobile, name, totalGiven: 0, totalReceived: 0, givenAssets: [], receivedAssets: [] };
      const amt = parseFloat(amount) || 0;
      if (type === "written") {
        if (amt > 0) map[key].totalGiven += amt;
        // asset handled separately below
      } else {
        if (amt > 0) map[key].totalReceived += amt;
      }
    };

    writtenData.forEach((r) => {
      upsert(r.mobile, r.name, "written", r.amount);
      if (r.asset_name && (!r.amount || parseFloat(r.amount) === 0)) {
        const key = `${r.mobile}||${r.name}`;
        if (!map[key]) map[key] = { mobile: r.mobile, name: r.name, totalGiven: 0, totalReceived: 0, givenAssets: [], receivedAssets: [] };
        map[key].givenAssets.push(r.asset_name);
      }
    });

    receivedData.forEach((r) => {
      upsert(r.mobile, r.name, "received", r.amount);
      if (r.asset_name && (!r.amount || parseFloat(r.amount) === 0)) {
        const key = `${r.mobile}||${r.name}`;
        if (!map[key]) map[key] = { mobile: r.mobile, name: r.name, totalGiven: 0, totalReceived: 0, givenAssets: [], receivedAssets: [] };
        map[key].receivedAssets.push(r.asset_name);
      }
    });

    return Object.values(map).map((p) => ({
      ...p,
      balance: p.totalGiven - p.totalReceived, // +ve = they owe me, -ve = I owe them
    }));
  }, [writtenData, receivedData]);

  const totalIOwe     = summary.filter((p) => p.balance < 0).reduce((s, p) => s + Math.abs(p.balance), 0);
  const totalOwedToMe = summary.filter((p) => p.balance > 0).reduce((s, p) => s + p.balance, 0);
  const settled       = summary.filter((p) => p.balance === 0 && p.givenAssets.length === 0 && p.receivedAssets.length === 0).length;

  const filtered = useMemo(() => {
    let list = [...summary];
    if (filterTab === "owe")  list = list.filter((p) => p.balance < 0);
    if (filterTab === "owed") list = list.filter((p) => p.balance > 0);
    if (sortBy === "balance") list.sort((a, b) => Math.abs(b.balance) - Math.abs(a.balance));
    if (sortBy === "name")    list.sort((a, b) => a.name.localeCompare(b.name));
    return list;
  }, [summary, filterTab, sortBy]);
  const pendingExportColumns = [
    { header: "Name", getValue: (row) => row.name || "—" },
    { header: "Mobile", getValue: (row) => row.mobile || "—" },
    { header: "You Gave", getValue: (row) => formatMoney(row.totalGiven || 0) },
    { header: "You Received", getValue: (row) => formatMoney(row.totalReceived || 0) },
    { header: "Balance", getValue: (row) => formatMoney(Math.abs(row.balance || 0)) },
    {
      header: "Status",
      getValue: (row) =>
        row.balance > 0
          ? "They need to give you"
          : row.balance < 0
            ? "You need to give them"
            : "Settled",
    },
  ];

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-2xl mx-4 max-h-[90vh] flex flex-col">

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 shrink-0">
          <div>
            <h3 className="text-lg font-semibold text-gray-800">People Summary</h3>
            <p className="text-xs text-gray-400 mt-0.5">Simple money summary for all people</p>
          </div>
          <button onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-full text-gray-400 hover:bg-gray-100 transition text-xl">
            &times;
          </button>
        </div>

        {/* Top Summary Cards */}
        <div className="grid grid-cols-3 gap-3 px-6 pt-5 pb-4 shrink-0">
          <div className="bg-rose-50/50 border border-rose-100/60 rounded-xl p-3 text-center">
            <p className="text-[10px] text-rose-500 font-bold uppercase tracking-wider mb-1">You Owe</p>
            <p className="text-xl font-bold text-rose-600">{formatMoney(totalIOwe)}</p>
            <p className="text-[10px] text-rose-400 mt-0.5">{summary.filter((p) => p.balance < 0).length} people</p>
          </div>
          <div className="bg-emerald-50/50 border border-emerald-100/60 rounded-xl p-3 text-center">
            <p className="text-[10px] text-emerald-600 font-bold uppercase tracking-wider mb-1">Owed To You</p>
            <p className="text-xl font-bold text-emerald-600">{formatMoney(totalOwedToMe)}</p>
            <p className="text-[10px] text-emerald-500/70 mt-0.5">{summary.filter((p) => p.balance > 0).length} people</p>
          </div>
          <div className="bg-slate-50 border border-slate-100 rounded-xl p-3 text-center">
            <p className="text-[10px] text-slate-400 font-bold uppercase tracking-wider mb-1">Settled</p>
            <p className="text-xl font-bold text-slate-500">{settled}</p>
            <p className="text-[10px] text-slate-400 mt-0.5">people</p>
          </div>
        </div>

        {/* Filter + Sort Row */}
        <div className="flex items-center justify-between px-6 pb-3 shrink-0">
          {/* Filter tabs */}
          <div className="flex gap-1 bg-slate-100 rounded-lg p-1">
            {[
              { key: "all",  label: "All" },
              { key: "owed", label: "Owed To Me" },
              { key: "owe",  label: "I Owe" },
            ].map((f) => (
              <button key={f.key} onClick={() => setFilterTab(f.key)}
                className={`px-3 py-1 rounded-md text-xs font-bold transition
                  ${filterTab === f.key ? "bg-white text-slate-700 shadow-sm" : "text-slate-400 hover:text-slate-600"}`}>
                {f.label}
              </button>
            ))}
          </div>
          {/* Sort */}
          <div className="flex items-center gap-2">
            <span className="text-xs text-slate-400 font-medium">Sort:</span>
            <select value={sortBy} onChange={(e) => setSortBy(e.target.value)}
              className="text-xs border border-slate-200 rounded-lg px-2 py-1 focus:outline-none focus:ring-2 focus:ring-amber-500/20 focus:border-amber-600 bg-white text-slate-600 font-medium transition">
              <option value="balance">By Amount</option>
              <option value="name">By Name</option>
            </select>
          </div>
        </div>

        <div className="px-6 pb-4 shrink-0 flex flex-wrap gap-2">
          <button
            onClick={() =>
              exportRowsToPdf("People Summary", pendingExportColumns, filtered)
            }
            className="px-3 py-2 border border-slate-200 bg-white hover:bg-slate-50 text-slate-700 text-xs font-bold rounded-xl transition shadow-sm"
          >
            Export PDF
          </button>
          <button
            onClick={() =>
              exportRowsToExcel(
                "People Summary",
                pendingExportColumns,
                filtered,
                "people-summary",
              )
            }
            className="px-3 py-2 border border-slate-200 bg-white hover:bg-slate-50 text-slate-700 text-xs font-bold rounded-xl transition shadow-sm"
          >
            Export Excel
          </button>
        </div>

        {/* People List */}
        <div className="overflow-y-auto px-6 pb-6 space-y-2">
          {filtered.length === 0 ? (
            <div className="text-center py-12 text-gray-400">
              <p className="text-3xl mb-2">🎉</p>
              <p className="text-sm font-medium">No pending entries here.</p>
            </div>
          ) : (
            filtered.map((p, i) => {
              const isPositive = p.balance > 0;
              const isNegative = p.balance < 0;
              const isSettled  = p.balance === 0;
              const hasAssets  = p.givenAssets.length > 0 || p.receivedAssets.length > 0;

              return (
                <div key={i} className={`rounded-xl border px-4 py-3 transition ${
                  isPositive ? "border-emerald-100 bg-emerald-50/20"
                  : isNegative ? "border-rose-100 bg-rose-50/20"
                  : "border-slate-100 bg-slate-50/40"
                }`}>
                  <div className="flex items-center justify-between">
                    {/* Left: Name + Mobile */}
                    <div>
                      <p className="text-xs font-bold text-slate-800">{p.name}</p>
                      <p className="text-[10px] text-slate-400 mt-0.5">{p.mobile}</p>
                      {/* Asset badges */}
                      {hasAssets && (
                        <div className="flex flex-wrap gap-1 mt-2">
                          {p.givenAssets.map((a, j) => (
                            <span key={`g${j}`} className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full bg-rose-50 border border-rose-200/50 text-[10px] font-semibold text-rose-600">
                              🎁 gave: {a}
                            </span>
                          ))}
                          {p.receivedAssets.map((a, j) => (
                            <span key={`r${j}`} className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full bg-emerald-50 border border-emerald-200/50 text-[10px] font-semibold text-emerald-700">
                              🎁 got: {a}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Right: Balance */}
                    <div className="text-right ml-4 shrink-0">
                      {isSettled && !hasAssets ? (
                        <span className="text-[10px] font-bold text-slate-400 bg-slate-100 px-2 py-1 rounded-lg">Settled ✓</span>
                      ) : (
                        <>
                          <p className={`text-sm font-bold ${isPositive ? "text-emerald-600" : isNegative ? "text-rose-600" : "text-gray-400"}`}>
                            {isSettled ? "—" : `${isPositive ? "+" : "-"}${formatMoney(Math.abs(p.balance))}`}
                          </p>
                          <p className={`text-[10px] mt-0.5 font-medium ${isPositive ? "text-emerald-500" : isNegative ? "text-rose-500" : "text-slate-400"}`}>
                            {isPositive ? "they owe you" : isNegative ? "you owe them" : ""}
                          </p>
                        </>
                      )}
                      {/* Given / Received breakdown */}
                      {(p.totalGiven > 0 || p.totalReceived > 0) && (
                        <p className="text-xs text-gray-400 mt-1">
                          ↑{formatMoney(p.totalGiven)} · ↓{formatMoney(p.totalReceived)}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────
function MoiList() {
  const dispatch = useDispatch();

  const [activeTab, setActiveTab] = useState("written");
  const [search, setSearch] = useState("");
  const [writtenData, setWrittenData] = useState([]);
  const [receivedData, setReceivedData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isTallyModalOpen, setIsTallyModalOpen] = useState(false);
  const [isPendingModalOpen, setIsPendingModalOpen] = useState(false);

  useEffect(() => {
    dispatch(setActivePage({ headerName: "My Money Notes", activeMenu: "moiNote" }));
  }, [dispatch]);

  const fetchMoiData = async () => {
    dispatch(setShowPreloader(true));
    setLoading(true);
    const getMoi = await AxiosGetMethod("customer/get-moi-note");
    setLoading(false);
    dispatch(setShowPreloader(false));
    if (getMoi?.status === 200) {
      // API expected to return: { data: { written: [...], received: [...] } }
      setWrittenData(getMoi.data.data.written || []);
      setReceivedData(getMoi.data.data.received || []);
    } else {
      if (getMoi?.status === 401) HandleLogout();
      ErrorToast("Something went wrong on fetching Moi data");
    }
  };

  useEffect(() => { fetchMoiData(); }, [dispatch]);

  const activeData = activeTab === "written" ? writtenData : receivedData;

  const filteredData = useMemo(() => {
    if (!search) return activeData;
    const s = search.toLowerCase();
    return activeData.filter((item) =>
      (item.event_type || "").toLowerCase().includes(s) ||
      (item.name || "").toLowerCase().includes(s) ||
      (item.family_name || "").toLowerCase().includes(s) ||
      (item.village || "").toLowerCase().includes(s) ||
      (item.mobile || "").includes(s)
    );
  }, [search, activeData]);
  const moiExportColumns = useMemo(() => buildMoiExportColumns(), []);
  const activeTitle = activeTab === "written" ? "Money You Gave" : "Money You Got";
  const hasSearchFilter = search.trim().length > 0;

  const handleMoiExport = (format) => {
    const rows = hasSearchFilter ? filteredData : activeData;
    if (!rows.length) {
      ErrorToast(`No ${hasSearchFilter ? "filtered" : ""} entries available to export.`.replace("  ", " ").trim());
      return;
    }

    const exportTitle = `${activeTitle} - ${hasSearchFilter ? "Filtered Results" : "All Entries"}`;
    const exportFileName = `${activeTab}-${hasSearchFilter ? "filtered" : "all"}`;

    if (format === "pdf") {
      exportRowsToPdf(exportTitle, moiExportColumns, rows);
      return;
    }

    exportRowsToExcel(exportTitle, moiExportColumns, rows, exportFileName);
  };

  const columns = [
    { name: "Event Date", selector: (row) => formatCustomDate(row.event_date), sortable: true, minWidth: "110px" },
    { name: "Event Type", selector: (row) => row.event_type, sortable: true, minWidth: "120px" },
    { name: "Name", selector: (row) => row.name, sortable: true, minWidth: "120px" },
    { name: "Father / Husband Name", selector: (row) => row.family_name, sortable: true, minWidth: "160px" },
    {
      name: "Amount",
      selector: (row) => row.amount ? formatMoney(row.amount) : "—",
      sortable: true,
      minWidth: "100px",
    },
    { name: "Asset", selector: (row) => row.asset_name || "—", minWidth: "110px" },
    { name: "Village", selector: (row) => row.village, sortable: true, minWidth: "120px" },
    { name: "Mobile", selector: (row) => row.mobile, minWidth: "110px" },
  ];

  const customStyles = {
    table: {
      style: {
        backgroundColor: "#ffffff",
      },
    },
    headRow: {
      style: {
        borderTop: "1px solid #f1f5f9",
        borderBottom: "1px solid #e2e8f0",
        backgroundColor: "#f8fafc",
        minHeight: "48px",
      },
    },
    headCells: {
      style: {
        color: "#475569",
        fontWeight: "750",
        fontSize: "11px",
        textTransform: "uppercase",
        letterSpacing: "0.05em",
        paddingLeft: "12px",
        paddingRight: "12px",
      },
    },
    rows: {
      style: {
        minHeight: "48px",
        fontSize: "12.5px",
        color: "#334155",
        "&:nth-of-type(even)": {
          backgroundColor: "#f8fafc",
        },
        "&:hover": {
          backgroundColor: "rgba(217,119,6,0.04) !important",
          transition: "background-color 0.15s ease",
        },
      },
    },
    cells: {
      style: {
        paddingLeft: "12px",
        paddingRight: "12px",
        borderBottom: "1px solid #f1f5f9",
      },
    },
    pagination: {
      style: {
        backgroundColor: "#ffffff",
        borderTop: "1px solid #e2e8f0",
        color: "#475569",
        fontSize: "11px",
        padding: "8px 16px",
      },
      pageButtonsStyle: {
        color: "#475569",
        fill: "#475569",
        borderRadius: "8px",
        transition: "all 0.2s ease",
        "&:hover:not(:disabled)": {
          backgroundColor: "#f1f5f9",
          color: "#d97706",
          fill: "#d97706",
        },
        "&:focus": {
          outline: "none",
          backgroundColor: "#f1f5f9",
        },
      },
    },
  };

  const tabs = [
    { key: "written",  label: "Money You Gave",  icon: "↑", activeClass: "border-amber-600 text-amber-700 font-bold",    countClass: "bg-amber-50 text-amber-700 border border-amber-200/50" },
    { key: "received", label: "Money You Got", icon: "↓", activeClass: "border-amber-600 text-amber-700 font-bold", countClass: "bg-slate-50 text-slate-600 border border-slate-200/50" },
  ];

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      <div className="bg-white rounded-2xl border border-slate-100 shadow-[0_4px_20px_-4px_rgba(0,0,0,0.05)] overflow-hidden">
        
        {/* Page Title + Action Buttons */}
        <div className="p-6 border-b border-slate-100 bg-slate-50/50">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
            <div>
              <h1 className="text-xl font-bold text-slate-800">My Money Notes</h1>
              <p className="text-xs text-slate-500 mt-1">
                Record and manage gifts (Moi) received and given at events
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <button
                onClick={() => setIsPendingModalOpen(true)}
                className="flex items-center gap-1.5 px-4 py-2 border border-slate-200 text-slate-700 bg-white text-xs font-bold rounded-xl hover:bg-slate-50 transition shadow-sm"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2z" />
                </svg>
                Summary
              </button>
              <button
                onClick={() => setIsTallyModalOpen(true)}
                className="flex items-center gap-1.5 px-4 py-2 border border-slate-200 text-slate-700 bg-white text-xs font-bold rounded-xl hover:bg-slate-50 transition shadow-sm"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 11h.01M12 11h.01M15 11h.01M4 19h16a2 2 0 002-2V7a2 2 0 00-2-2H4a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
                Check Person
              </button>
              <button
                onClick={() => setIsAddModalOpen(true)}
                className="flex items-center gap-1.5 px-4 py-2 bg-amber-600 text-white text-xs font-bold rounded-xl hover:bg-amber-700 transition shadow-sm"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
                </svg>
                Add New Entry
              </button>
            </div>
          </div>
        </div>

        <div className="p-6">
          {/* Tabs */}
          <div className="flex border-b border-slate-200 mb-6">
            {tabs.map((tab) => (
              <button
                key={tab.key}
                onClick={() => { setActiveTab(tab.key); setSearch(""); }}
                className={`flex items-center gap-1.5 px-4 py-3 text-xs font-bold border-b-2 transition -mb-px
                  ${activeTab === tab.key ? tab.activeClass : "border-transparent text-slate-400 hover:text-slate-600"}`}
              >
                <span className="text-sm">{tab.icon}</span>
                {tab.label}
                <span className={`ml-1.5 text-[9px] px-1.5 py-0.5 rounded-full font-bold
                  ${activeTab === tab.key ? tab.countClass : "bg-slate-100 text-slate-400"}`}>
                  {tab.key === "written" ? writtenData.length : receivedData.length}
                </span>
              </button>
            ))}
          </div>

          {/* Summary Bar */}
          <SummaryBar data={activeData} type={activeTab} />

          {/* Search + Export */}
          <div className="flex flex-col gap-3 mb-6 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => handleMoiExport("pdf")}
                className="px-3 py-2 border border-slate-200 bg-white hover:bg-slate-50 text-slate-700 text-xs font-bold rounded-xl transition shadow-sm"
              >
                Export PDF
              </button>
              <button
                onClick={() => handleMoiExport("excel")}
                className="px-3 py-2 border border-slate-200 bg-white hover:bg-slate-50 text-slate-700 text-xs font-bold rounded-xl transition shadow-sm"
              >
                Export Excel
              </button>
            </div>
            <input
              type="text"
              placeholder="🔍 Search name, event, mobile..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="p-2.5 text-xs border border-slate-200 rounded-xl bg-slate-50 focus:bg-white focus:outline-none focus:ring-2 focus:ring-amber-500/20 focus:border-amber-600 transition w-full sm:w-72"
            />
          </div>

          {/* Data Table */}
          <DataTable
            columns={columns}
            data={filteredData}
            pagination
            progressPending={loading}
            highlightOnHover
            customStyles={customStyles}
            noDataComponent={
              <div className="py-12 text-center text-slate-400 text-xs">
                <p className="text-2xl mb-2">{activeTab === "written" ? "📤" : "📥"}</p>
                <p className="font-semibold">No {activeTab === "written" ? "given" : "received"} entries found</p>
              </div>
            }
          />
        </div>
      </div>

      {/* Modals */}
      <AddMoiModal
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        onSuccess={fetchMoiData}
        moiType={activeTab}
      />
      <TallyModal
        isOpen={isTallyModalOpen}
        onClose={() => setIsTallyModalOpen(false)}
        writtenData={writtenData}
        receivedData={receivedData}
      />
      <PendingModal
        isOpen={isPendingModalOpen}
        onClose={() => setIsPendingModalOpen(false)}
        writtenData={writtenData}
        receivedData={receivedData}
      />
    </div>
  );
}

export default MoiList;
